import React from 'react';

import {ELEMENT_TYPE, FILTER_SECTION, FILTER_TYPE} from '../../../constants';
import HeaderBar, {buildThirdLevelNavHeader} from '../../../forecast-app/shared/components/headers/header-bar/header_bar';
import {handleLayoutDetailSelect} from './people_scheduling_options';
import Util from '../../../forecast-app/shared/util/util';
import {FILTER_SECTIONS} from '../../../forecast-app/shared/components/filters/FilterWrapper';
import {TopHeaderBar} from '../../../forecast-app/shared/components/headers/top-header-bar/TopHeaderBar';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {hasFeatureFlag} from '../../../forecast-app/shared/util/FeatureUtil';
import {UTILIZATION_FORMAT} from '../canvas-timeline/canvas_timeline_util';

const mapFilterV4ViewerDataToGraphQLType = pageComponent => {
	const {personFilters, projectFilters} = pageComponent.getFilterData();

	return {
		filters: {edges: [...personFilters, ...projectFilters].map(filter => ({node: {...filter}}))},
	};
};

export const getTopHeaderContent = pageComponent => {
	const isUsingProjectAllocation = pageComponent.state.data
		? pageComponent.state.data.company.isUsingProjectAllocation
		: pageComponent.state.lightWeightData.company.isUsingProjectAllocation;

	const content = [];
	if (
		pageComponent.getViewer().availableFeatureFlags &&
		pageComponent.getViewer().availableFeatureFlags.some(flag => flag.key === 'hide_onboarding_container')
	) {
		const onboardingFlows = [
			{
				id: 'schedule-people-introduction',
				title: 'Schedule (People View) - Intro',
				description: null,
				contentId: '1594208602nMuv6427',
			},
		];
		if (hasPermission(PERMISSION_TYPE.SCHEDULING_ACCESS)) {
			if (!isUsingProjectAllocation) {
				onboardingFlows.push({
					id: 'schedule-people-unassigned',
					title: 'Schedule (People View) - Unassigned Tasks',
					description: null,
					contentId: '1594284240gDds8911',
				});
			}
			onboardingFlows.push({
				id: 'schedule-people-adding',
				title: 'Schedule (People View) - Adding People to Work',
				description: null,
				contentId: '1594288382tBop8254',
			});
		}
		onboardingFlows.push({
			id: 'schedule-people-filter',
			title: 'Filtering',
			description: null,
			contentId: '1610444655dQve1546',
		});

		const onboardingComponent = {
			id: 'onboarding-component',
			intl: pageComponent.props.intl,
			type: TopHeaderBar.TYPE.ONBOARDING,
			title: 'Learn to use the Schedule (People View)',
			options: onboardingFlows,
			helpCenterLink: 'https://help.forecast.it/schedule-people-view-walkthrough',
		};
		content.push(onboardingComponent);
	}
	return content;
};

export const GROUP_BY = {
	NONE: 'none',
	ROLE: 'role',
	DEPARTMENT: 'department',
};

export const createHeader = pageComponent => {
	const {peopleSchedulingDetailSelectorOptions} = pageComponent.state;
	const intl = pageComponent.props.intl;
	const isUsingProjectAllocation = pageComponent.state.data
		? pageComponent.state.data.company.isUsingProjectAllocation
		: pageComponent.state.lightWeightData.company.isUsingProjectAllocation;
	const useThirdLevelNav = pageComponent.state.initData.featureFlags.some(flag => flag.key === 'third_level_nav');

	const leftContent = [],
		rightContent = [];

	leftContent.push({
		type: ELEMENT_TYPE.EMPTY_SPACE,
		width: 14,
	});

	if (hasFeatureFlag('resource_utilization_format')) {
		const dropdownOptions = [
			{value: UTILIZATION_FORMAT.HOURS, label: intl.formatMessage({id: 'scheduling.heatmap_in_hours'})},
			{value: UTILIZATION_FORMAT.PERCENTAGE, label: intl.formatMessage({id: 'scheduling.heatmap_in_percentage'})},
		];

		leftContent.push({
			type: ELEMENT_TYPE.DROPDOWN,
			dropdownOptions,
			value: pageComponent.state.peopleSchedulingOptions.utilizationFormat,
			callback: format => pageComponent.handleUtilizationFormatChange(format.value),
			cy: 'utilization-resource-format-changer',
		});
	}

	leftContent.push({type: ELEMENT_TYPE.EXPAND_ALL_CANVAS, cy: 'expand-all-button'});

	leftContent.push({
		type: ELEMENT_TYPE.DATE_CHANGER,
		onlyTodayButton: true,
		handleTodayButtonClick: pageComponent.scrollToToday.bind(pageComponent),
		handleMoveDateButtonClick: pageComponent.handleMoveDateButtonClick.bind(pageComponent),
		userpilot: 'schedule-date-changer',
		cy: 'date-changer',
	});

	leftContent.push({
		type: ELEMENT_TYPE.ZOOM_MENU,
	});

	rightContent.push({
		type: ELEMENT_TYPE.SEARCH,
		value: pageComponent.state.searchFilterValue || '',
		onChange: pageComponent.onSearchChange.bind(pageComponent),
		cy: 'canvas-search',
	});

	const data = pageComponent.state.data ? pageComponent.state.data : pageComponent.state.lightWeightData;

	const dropdownOptions = [
		{value: GROUP_BY.NONE, label: intl.formatMessage({id: 'common.no_grouping'})},
		{value: GROUP_BY.ROLE, label: intl.formatMessage({id: 'common.group_by_role'})},
	];
	if (data && data.modules && Util.hasDepartments(data.modules) && data.departments && data.departments.length > 0) {
		dropdownOptions.push({value: GROUP_BY.DEPARTMENT, label: intl.formatMessage({id: 'common.group_by_department'})});
	}
	rightContent.push({
		type: ELEMENT_TYPE.DROPDOWN,
		dropdownOptions,
		value: pageComponent.state.peopleSchedulingOptions.groupBy,
		callback: group => pageComponent.handleGroupByChange(group.value),
		cy: 'canvas-search',
	});

	rightContent.push({
		type: ELEMENT_TYPE.THE_EYE,
		options: peopleSchedulingDetailSelectorOptions,
		onSelect: selected => handleLayoutDetailSelect(pageComponent, selected),
		expandLeft: true,
		disableTooltip: true,
	});

	const filterString = `canvas-scheduling-filters-v4-people`;

	const projectFilters = [
		FILTER_TYPE.PROJECT,
		FILTER_TYPE.CLIENT,
		FILTER_TYPE.PERSON,
		FILTER_TYPE.PROJECT_STAGE,
		FILTER_TYPE.PROJECT_STATUS,
		FILTER_TYPE.CONTACT,
		FILTER_TYPE.LABEL,
	];
	const taskFilters = !isUsingProjectAllocation
		? [
				FILTER_TYPE.ROLE,
				FILTER_TYPE.STATUS_CATEGORY,
				FILTER_TYPE.INDICATOR,
				FILTER_TYPE.OWNER,
				FILTER_TYPE.PROJECT_FOLLOWER,
				FILTER_TYPE.RECENT_ACTIVITY,
				FILTER_TYPE.LABEL,
		  ]
		: undefined;

	const peopleFilters = hasPermission(PERMISSION_TYPE.SCHEDULING_ACCESS)
		? [
				FILTER_TYPE.PROJECT,
				FILTER_TYPE.PERSON,
				FILTER_TYPE.TEAM,
				FILTER_TYPE.ROLE,
				FILTER_TYPE.CAPACITY_WORKLOAD,
				FILTER_TYPE.LABEL,
		  ]
		: undefined;

	if (peopleFilters && Util.hasSkills() && hasFeatureFlag('skills')) {
		peopleFilters.push(FILTER_TYPE.SKILL);
	}

	const {modules} = pageComponent.getFilterData();

	const hasFinancialModule = Util.hasFinance(modules);

	if (hasFinancialModule && projectFilters) {
		projectFilters.splice(1, 0, FILTER_TYPE.PROJECT_TYPE);
	}

	if (peopleFilters && Util.hasDepartments(modules)) {
		peopleFilters.push(FILTER_TYPE.DEPARTMENT);
	}

	const viewer = mapFilterV4ViewerDataToGraphQLType(pageComponent);
	// Map filter options from V3 -> V4
	if (viewer.filters && viewer.filters.edges) {
		viewer.filters.edges
			.map(edge => edge.node)
			.filter(filter => filter.section === 'SCHEDULING_PEOPLE')
			.forEach(filter => {
				const filterValue = JSON.parse(filter.value);
				if (filterValue && filterValue.person && filterValue.person.team) {
					filterValue.person.team = filterValue.person.team.map(teamId => {
						if (typeof teamId === 'number') {
							return btoa(`Team:${teamId}`);
						} else {
							return teamId;
						}
					});
					filter.value = JSON.stringify(filterValue);
				}
			});
	}

	if (projectFilters) {
		projectFilters.push(FILTER_TYPE.INTERNAL_TIME);
		projectFilters.push(FILTER_TYPE.TIME_OFF);
	}
	if (peopleFilters) {
		peopleFilters.push(FILTER_TYPE.INTERNAL_TIME);
		peopleFilters.push(FILTER_TYPE.TIME_OFF);
	}

	if (projectFilters || peopleFilters || taskFilters) {
		const preselectedSection = hasPermission(PERMISSION_TYPE.SCHEDULING_ACCESS)
			? FILTER_SECTIONS.PEOPLE
			: taskFilters
			? FILTER_SECTIONS.TASKS
			: FILTER_SECTIONS.PROJECTS;

		rightContent.push({
			type: ELEMENT_TYPE.FILTER_V4,
			defaultSection: preselectedSection,
			projectFilters,
			peopleFilters,
			taskFilters,
			viewer,
			appliedFiltersName: filterString,
			filterSection: FILTER_SECTION.SCHEDULING_PEOPLE,
			onFiltersChange: pageComponent.onFilterChange.bind(pageComponent),
			projectId: pageComponent.state.viewer.project ? pageComponent.state.viewer.project.id : null,
			projectGroupId: pageComponent.state.viewer.projectGroup ? pageComponent.state.viewer.projectGroup.id : null,
			userpilot: 'filter-button',
			cy: 'people-schedule-filters',
		});
	}

	if (
		pageComponent.getViewer().availableFeatureFlags &&
		!useThirdLevelNav &&
		pageComponent.getViewer().availableFeatureFlags.some(flag => flag.key === 'hide_onboarding_container')
	) {
		const onboardingFlows = [
			{
				id: 'schedule-people-introduction',
				title: 'Schedule (People View) - Intro',
				description: null,
				contentId: '1594208602nMuv6427',
			},
		];
		if (hasPermission(PERMISSION_TYPE.SCHEDULING_ACCESS)) {
			if (!isUsingProjectAllocation) {
				onboardingFlows.push({
					id: 'schedule-people-unassigned',
					title: 'Schedule (People View) - Unassigned Tasks',
					description: null,
					contentId: '1594284240gDds8911',
				});
			}
			onboardingFlows.push({
				id: 'schedule-people-adding',
				title: 'Schedule (People View) - Adding People to Work',
				description: null,
				contentId: '1594288382tBop8254',
			});
		}
		onboardingFlows.push({
			id: 'schedule-people-filter',
			title: 'Filtering',
			description: null,
			contentId: '1610444655dQve1546',
		});

		const onboardingComponent = {
			id: 'onboarding-component',
			intl: pageComponent.props.intl,
			type: ELEMENT_TYPE.ONBOARDING_POPUP,
			title: 'Learn to use the Schedule (People View)',
			options: onboardingFlows,
			helpCenterLink: 'https://help.forecast.it/schedule-people-view-walkthrough',
		};
		rightContent.push(onboardingComponent);
	}
	if (useThirdLevelNav) {
		return buildThirdLevelNavHeader(leftContent, rightContent, null, {scheduling: true});
	}
	return <HeaderBar scheduling={true} noPadding={true} leftContent={leftContent} rightContent={rightContent} />;
};
